/** Private on-screen reading, using the existing owner-scoped mail API.
 * Message text never becomes HTML, model input, local storage or diagnostics.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import MailComposer from "../../mail/MailComposer";
import { useAuth } from "../../../context/AuthContext";
import { isMailReference, mailReaderHref } from "../../../utils/mailReaderReference";

const ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const MAIL_API = `${ORIGIN}/api/mail-connections/icloud`;
// A separate instance excludes global offline and diagnostic interceptors.
const mailClient = axios.create({ withCredentials: true, timeout: 35000 });
const text = (value) => typeof value === "string" ? value : "";

function readError(error) {
  if (error?.readerConnectionChanged) return "The connected mailbox changed. Ask Karen to search again before opening this email.";
  switch (error?.response?.status) {
    case 401: return "Your NDG session has expired. Sign in again to open this email.";
    case 403: return "This NDG account cannot open that email connection.";
    case 404: return "This email or connection is no longer available. Ask Karen to search again.";
    case 409: return "The mailbox or sign-in changed. Ask Karen to search again.";
    case 429: return "Email is busy. Try opening the message again.";
    case 503: return "The saved connection needs checking in Email connections.";
    default: return "The email could not be opened just now. Try again; no email was changed.";
  }
}

function MailReader({ presentation, accountKey, onExpand, standalone, expectedConnection }) {
  const data = presentation?.data;
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [connectionVersion, setConnectionVersion] = useState(null);
  const connectionRef = useRef(expectedConnection || null);
  const headingRef = useRef(null);

  useEffect(() => {
    setLoaded(null); setError(""); setLoading(false);
    if (Array.isArray(data?.results)) {
      setRows(data.results.filter((row) => row && isMailReference(row.id)).slice(0, 50));
      setSelectedId(null);
    } else if (isMailReference(data?.id)) {
      setSelectedId(data.id);
      setRetry((n) => n + 1);
    } else {
      setRows([]); setSelectedId(null);
    }
  }, [data]);

  useEffect(() => {
    if (!selectedId || !accountKey) return undefined;
    let active = true;
    const controller = new AbortController();
    const options = { signal: controller.signal, headers: { "Cache-Control": "no-store" } };
    setLoading(true); setLoaded(null); setError("");
    onExpand?.();
    (async () => {
      const { data: connection } = await mailClient.get(`${MAIL_API}/status`, options);
      if (!active) return;
      if (connection?.provider !== "icloud" || connection?.connected !== true
          || connection?.configured !== true || typeof connection?.verified_at !== "string") {
        throw new Error("Connection not available");
      }
      if (connectionRef.current && connectionRef.current !== connection.verified_at) {
        const changed = new Error("Connection changed");
        changed.readerConnectionChanged = true;
        throw changed;
      }
      connectionRef.current = connection.verified_at;
      setConnectionVersion(connection.verified_at);
      const { data: message } = await mailClient.get(
        `${MAIL_API}/messages/${encodeURIComponent(selectedId)}`, options,
      );
      if (message?.access !== "read_only" || typeof message?.mailbox !== "string"
          || message?.id !== selectedId || typeof message?.text !== "string") {
        throw new Error("Unexpected private mail response");
      }
      if (active) setLoaded(message);
    })().catch((failure) => {
      if (active) setError(readError(failure));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [selectedId, accountKey, retry, onExpand]);

  useEffect(() => {
    if (!loaded) return;
    headingRef.current?.focus?.({ preventScroll: true });
    const scroller = headingRef.current?.closest('[data-testid="universal-intent-response-body"]');
    if (scroller) scroller.scrollTop = 0;
  }, [loaded]);

  const selectedRow = rows.find((row) => row.id === selectedId);
  const message = loaded?.id === selectedId ? loaded : null;
  const excerpt = data?.id === selectedId ? text(data?.text) : "";
  const separateHref = message && !standalone
    ? mailReaderHref(selectedId, accountKey, connectionVersion) : null;

  if (selectedId) {
    return <section data-testid="karen-mail-reader" aria-busy={loading} className="karen-mail-reader">
      <div className="karen-mail-toolbar">
        {rows.length > 0 && <button type="button" className="karen-panel-button"
          onClick={() => { setSelectedId(null); setLoaded(null); setError(""); }}>
          Back to email results
        </button>}
        {separateHref && <a className="karen-panel-button" href={separateHref}
          target="_blank" rel="noopener noreferrer" data-testid="karen-mail-separate-page">
          Open on separate page
        </a>}
      </div>
      <h3 ref={headingRef} tabIndex={-1} className="karen-mail-subject">
        {text(message?.subject) || text(selectedRow?.subject) || text(data?.subject) || "Email"}
      </h3>
      {message && <dl className="karen-mail-metadata">
        <div><dt>From</dt><dd>{text(message.from) || "Not supplied"}</dd></div>
        <div><dt>Date</dt><dd>{text(message.date) || "Not supplied"}</dd></div>
        {text(message.to) && <div><dt>To</dt><dd>{message.to}</dd></div>}
      </dl>}
      {loading && <p role="status">Opening your email…</p>}
      {error && <div role="alert" className="karen-mail-error">
        <p>{error}</p>
        <button type="button" className="karen-panel-button" onClick={() => setRetry((n) => n + 1)}>Try opening again</button>
        {excerpt && <><p>Only the previously supplied excerpt is shown below.</p>
          <div className="karen-mail-text">{excerpt}</div></>}
      </div>}
      {message && <>
        {message.truncated && <p role="note" className="karen-mail-limit">
          Partial retrieval: this message exceeded the current download limit. Open the original in your mail app for anything missing.
        </p>}
        <div data-testid="karen-full-email-text" className="karen-mail-text">
          {message.text || "This email has no readable message text. Attachments are not opened here."}
        </div>
        <MailComposer key={selectedId} sourceId={selectedId} controlsOnly expectedConnection={connectionVersion} />
        <p className="karen-mail-privacy">Message text only. Attachments and remote images are not loaded. Opening this view does not mark the email as read.</p>
      </>}
    </section>;
  }

  return <section data-testid="karen-mail-results" className="karen-mail-results">
    <p className="karen-mail-help">Tap an email to open it here, or ask Karen to read a numbered result.</p>
    {rows.length === 0 ? <p>{presentation?.narration || "No email results were supplied."}</p>
      : <ol className="karen-mail-result-list">{rows.map((row, index) => <li key={row.id}>
        <button type="button" className="karen-mail-result"
          aria-label={`Open email ${index + 1}: ${text(row.subject) || "No subject"}`}
          onClick={() => { setLoaded(null); setError(""); setSelectedId(row.id); onExpand?.(); }}>
          <span className="karen-mail-result-title">{index + 1}. {text(row.subject) || "No subject"}</span>
          <span>{text(row.from)}</span><span>{text(row.date)} · {text(row.mailbox)}</span>
          {row.relevance?.status === "verified" && <span>Match in {text(row.relevance.location)}: {text(row.relevance.excerpt)}</span>}
          {row.relevance?.status === "unverified" && <span>Possible match — {text(row.relevance.reason)}</span>}
          <span className="karen-mail-open-label">Open email</span>
        </button>
      </li>)}</ol>}
    <p className="karen-mail-help">{data?.has_more ? "More messages remain to check. Say more results to continue." : ""}{data?.coverage?.failed_folders?.length > 0 ? " Some folders could not be searched; results are incomplete." : ""}{data?.coverage?.unchecked_messages > 0 ? " Some large emails remain possible matches because they could not be checked completely." : ""}</p>
    <p className="karen-mail-privacy">Results are grouped by email folder. Opening one does not mark it as read.</p>
  </section>;
}

export default function MailAnswer(props) {
  const { user, loading } = useAuth();
  const accountKey = text(user?.id || user?._id);
  // Never reuse an existing presentation after an account transition.
  const originalAccount = useRef(accountKey);
  if (loading || !accountKey || !["adi", "pdi", "admin"].includes(user?.role)) {
    return <p role="status">Sign in to your NDG instructor account to open an email.</p>;
  }
  if (originalAccount.current !== accountKey || (props.expectedOwner && props.expectedOwner !== accountKey)) {
    return <p role="alert">Your NDG account changed. Close this view and search again.</p>;
  }
  if (props.presentation?.data?.compose === true) {
    return <MailComposer key={text(props.presentation.data.draft_request_id) || "manual-compose"}
      sourceId={props.presentation.data.source_id} expectedConnection={props.presentation.data.connection_version}
      initialInstruction={text(props.presentation.data.draft_instruction)}
      initialRequestId={text(props.presentation.data.draft_request_id)}
      initialRecipient={text(props.presentation.data.draft_to)} />;
  }
  return <MailReader accountKey={accountKey} {...props} />;
}
